import dayjs from 'lib/dayjs';
import { Status } from './Status';
import { useTranslate } from '@tolgee/react';
import { Button, Divider, Popconfirm, Tooltip, message } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as AnchorSvg } from 'views/Conversation/Sider/assets/anchor.svg';
import { Solutions } from './Solutions';
import { LogHistory } from './LogHistory';
import { TypeSource } from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import { Note } from './Note';
import type { UseTicketListParams } from 'hooks/business/chat/useTicketMutations';
import {
  useCloseTodoMutation,
  useReopenTodoMutation,
} from 'hooks/business/chat/useTicketMutations';
import { ReplyMsg } from './ReplyMsg';
import { RelatedOrders } from './RelatedOrders';
import { RelatedItems } from './RelatedItems';
import { eventBus } from 'services/eventBus';
import { useMessageById } from 'hooks/business/message';
import useModal from 'hooks/useModal';
import { getCloseTodoValidation } from 'views/Conversation/Sider/ToDoBundle/todoBusinessUtils';
import { TransferTicketModal } from './TransferTicketModal';
import { isTodoCompleted } from 'views/Conversation/Sider/ToDoBundle/useResultForm';
import { ReturnOrders } from './ReturnOrders';
import { isReturnOrderEnable } from 'views/Conversation/Sider/ToDoBundle/helper';
import { SubTypes } from './SubTypes';
import { useEffect, useRef } from 'react';
import { DefaultButton } from '../DefaultButton';
import { deleteTodo } from 'views/Conversation/Sider/ToDoBundle/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { getPopupContainer } from 'components/LightConversation/utils/popover';

interface TicketDetailProps {
  ticket: Todo.TodoInstance;
  task: Todo.Task;
  policies: Todo.Policy[];
  typeSource: TypeSource;
  listParams: UseTicketListParams | null;
  conversationId: string;
  platform: General.PlatformUpperCase;
  externalBuyerId: string;
  cachedScrollTop: number;
  onSaveScrollTop: (ticketId: string, scrollTop: number) => void;
  onUpdate?: () => void;
  deleteEnabled?: boolean;
  transferEnabled?: boolean;
}

export const TicketDetail = (props: TicketDetailProps) => {
  const {
    ticket,
    task,
    policies,
    typeSource,
    listParams,
    conversationId,
    externalBuyerId,
    cachedScrollTop,
    onSaveScrollTop,
    onUpdate,
    deleteEnabled = true,
    transferEnabled = true,
  } = props;
  const {
    id,
    type,
    region,
    storeId,
    operator,
    todoStatus,
    operatorName,
    createTime,
    operationLog,
    operateResult,
    relatedMsgId,
    subType,
    subType2,
    subType3,
    subStatus,
  } = ticket;

  const { t } = useTranslate();
  const queryClient = useQueryClient();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const msg = useMessageById(relatedMsgId);

  const closeTodoMutation = useCloseTodoMutation(listParams);
  const reopenTodoMutation = useReopenTodoMutation(listParams);

  const { mutate: deleteTodoMutation, isLoading: isDeleteTodoLoading } = useMutation({
    mutationFn: () => deleteTodo(id),
    onSuccess: () => {
      if (listParams) {
        queryClient.invalidateQueries({
          queryKey: queries.tickets.list(listParams).queryKey,
        });
      }
      message.success(t('operation.deleteSucceed'));
      onUpdate?.();
    },
    onError: () => {
      message.error(t('operation.deleteFailed'));
    },
  });
  const { visible, show, hide } = useModal<string>();

  const handleClose = () => {
    const validation = getCloseTodoValidation(ticket);
    if (!validation.valid && validation.errorMessageKey) {
      const msgText =
        validation.errorMessageParams?.x != null
          ? t(validation.errorMessageKey, {
              x: t(validation.errorMessageParams.x),
            })
          : t(validation.errorMessageKey);
      message.error(msgText);
      return;
    }
    closeTodoMutation.mutate({ id });
  };
  const handleReopen = () => {
    reopenTodoMutation.mutate({ id });
  };

  const handleDelete = () => {
    deleteTodoMutation();
  };

  const isCompleted = isTodoCompleted(todoStatus);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = cachedScrollTop;
    }
  }, [id, cachedScrollTop]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    onSaveScrollTop(id, e.currentTarget.scrollTop);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div
        className="flex-1 flex flex-col px-3 py-4 overflow-y-auto bg-[var(--fett-bg-elevated)]"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <div className="flex items-center justify-between">
          <Status
            id={id}
            taskId={task.id}
            todoStatus={todoStatus}
            listParams={listParams}
            type={type}
            subStatus={subStatus}
          />
          {msg ? (
            <Tooltip
              arrowPointAtCenter
              placement="left"
              title={t('dump_to_msg')}
              getPopupContainer={getPopupContainer}
            >
              <Icon
                className="cursor-pointer"
                component={AnchorSvg}
                onClick={() => eventBus.emit('SCROLL_TO_MSG', msg.msgId)}
              />
            </Tooltip>
          ) : null}
        </div>
        <div className="flex items-center justify-between text-[var(--fett-text-tertiary)] text-xs mt-1">
          <span>
            {t('todo.受理客服')}: {operatorName || '-'}
          </span>
          <span>{dayjs(Number(createTime)).format('YYYY-MM-DD HH:mm:ss')}</span>
        </div>
        <Divider className="!my-4" />
        <SubTypes
          id={id}
          typeSource={typeSource}
          type={type}
          listParams={listParams}
          subType={[subType, subType2, subType3]}
          readonly={isCompleted}
        />
        <Solutions
          todo={ticket}
          task={task}
          policies={policies}
          typeSource={typeSource}
          listParams={listParams}
          readonly={isCompleted}
        />
        {!isCompleted && (
          <ReplyMsg
            typeSource={typeSource}
            id={id}
            region={region}
            type={type}
            operateResult={operateResult}
            conversationId={conversationId}
          />
        )}
        <Note
          ticket={ticket}
          listParams={listParams}
          conversationId={conversationId}
          readonly={isCompleted}
        />
        <RelatedOrders ticket={ticket} listParams={listParams} readonly={isCompleted} />
        <RelatedItems ticket={ticket} listParams={listParams} readonly={isCompleted} />
        {isReturnOrderEnable(operateResult, type) && (
          <ReturnOrders
            ticket={ticket}
            listParams={listParams}
            externalBuyerId={externalBuyerId}
            readonly={isCompleted}
          />
        )}
        <LogHistory type={type} operationLog={operationLog} storeId={storeId} />
      </div>
      <div className="flex-shrink-0 flex items-center justify-between bg-[var(--fett-bg-elevated)] py-2 px-4 shadow-tt">
        {deleteEnabled ? (
          <Popconfirm
            title={t('upload.你确定要删除吗')}
            okButtonProps={{ loading: isDeleteTodoLoading }}
            onConfirm={handleDelete}
          >
            <div className="text-[var(--fett-text-secondary)] cursor-pointer">
              {t('operation.delete')}
            </div>
          </Popconfirm>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-4">
          {isCompleted || !transferEnabled ? null : (
            <DefaultButton onClick={() => show(operator)}>
              {t('sidebar.taskTab.action.transfer')}
            </DefaultButton>
          )}
          {isCompleted ? (
            <Button type="primary" loading={reopenTodoMutation.isLoading} onClick={handleReopen}>
              {t('operateType.REOPEN')}
            </Button>
          ) : (
            <Button type="primary" loading={closeTodoMutation.isLoading} onClick={handleClose}>
              {t('operateType.CLOSE')}
            </Button>
          )}
        </div>
        <TransferTicketModal
          id={id}
          storeId={storeId}
          operator={operator}
          listParams={listParams}
          visible={visible}
          onCancel={hide}
        />
      </div>
    </div>
  );
};
